import { Injectable } from "@angular/core";
import { ConsultationService } from "./consultation.service";

@Injectable()
export class ZipDownloadService {
    constructor(private consultationService: ConsultationService) {}

    private getFinishedProcessIds(page: number) {
        return this.consultationService.getFinishedProcessIds(page).toPromise();
    }

    async *fetchDocuments(
        startAtProcessId = 0,
        startAtPage = 0,
    ): AsyncIterableIterator<DownloadProgress> {
        for (
            let processIds, page = startAtPage;
            (processIds = await this.getFinishedProcessIds(page)).length !== 0;
            page++
        ) {
            for (const processIdBatch of batchIterable(processIds, 5)) {
                const promises = processIdBatch
                    .filter(
                        (processId) =>
                            (processId as number) >= startAtProcessId,
                    )
                    .map(async (processId: number) => {
                        let error;
                        for (let i = 0; i < 3; i++) {
                            try {
                                const document = await this.consultationService
                                    .getDocumentByConsultationProcessId(
                                        processId,
                                    )
                                    .toPromise();

                                const url =
                                    this.consultationService.getDocumentUrl(
                                        document.Id,
                                    );
                                const name = document.Name;
                                const data = await this.consultationService
                                    .loadPDF(url)
                                    .toPromise();

                                return { page, processId, name, data };
                            } catch (err) {
                                error = err;
                            }
                        }
                        throw error;
                    });

                yield* await Promise.all(promises);
            }
        }
    }

    async *fetchZipFiles(
        startAtProcessId = 0,
        startAtPage = 0,
        splitAt: number = 20 * 1024 * 1024,
    ): AsyncIterableIterator<ZipProgress> {
        let zipWriter = await createZipWriter();

        let currentSize: number,
            currentPage: number,
            firstProcessId: number,
            lastProcessId: number;
        resetProgress();

        try {
            for await (const download of this.fetchDocuments(
                startAtProcessId,
                startAtPage,
            )) {
                if (currentSize + download.data.size > splitAt) {
                    yield* closeWriter(true);
                }
                await addToWriter(download);
                yield getProgress();
            }
        } finally {
            yield* closeWriter(false);
        }

        function getProgress(): ZipScope {
            return { page: currentPage, firstProcessId, lastProcessId };
        }

        function updateProgress(
            size = 0,
            page: number = null,
            processId: number = null,
        ): void {
            currentSize += size;
            currentPage = page;
            if (firstProcessId === null) {
                firstProcessId = processId;
            }
            lastProcessId = processId;
        }

        function resetProgress() {
            currentSize = 0;
            currentPage = null;
            firstProcessId = null;
            lastProcessId = null;
        }

        async function addToWriter(document: DownloadProgress): Promise<void> {
            const { name, data, page, processId } = document;
            await addBlobToZipWriter(zipWriter, name, data);
            updateProgress(data.size, page, processId);
        }

        async function* closeWriter(paused: boolean) {
            if (currentSize > 0) {
                yield {
                    ...getProgress(),
                    data: await closeZipWriter(zipWriter),
                };

                if (paused) {
                    zipWriter = await createZipWriter();
                    resetProgress();
                }
            } else if (!paused) {
                await closeZipWriter(zipWriter);
            }
        }
    }
}

function* batchIterable<T>(
    iterable: Iterable<T>,
    batchSize: number,
): IterableIterator<T[]> {
    let batch = [];

    for (const value of iterable) {
        batch.push(value);
        if (batch.length >= batchSize) {
            yield batch;
            batch = [];
        }
    }

    if (batch.length) {
        yield batch;
    }
}

declare const zip: any;
zip.workerScripts = {
    deflater: ["zip/z-worker.js", "zip/deflate.js"],
};

function createZipWriter(): Promise<any> {
    return new Promise((resolve, reject) =>
        zip.createWriter(new zip.BlobWriter(), resolve, reject),
    );
}

function addBlobToZipWriter(zipWriter, filename, blob): Promise<void> {
    return new Promise((resolve) =>
        zipWriter.add(filename, new zip.BlobReader(blob), resolve),
    );
}

function closeZipWriter(zipWriter): Promise<Blob> {
    return new Promise((resolve) => zipWriter.close(resolve));
}

export interface ZipProgress extends ZipScope {
    data?: Blob;
}

interface ZipScope extends ZipOffset {
    lastProcessId: number;
}

interface ZipOffset {
    page: number;
    firstProcessId: number;
}

export interface DownloadProgress extends DownloadOffset, Download {}

interface Download {
    name: string;
    data: Blob;
}

interface DownloadOffset {
    page: number;
    processId: number;
}
